import React, { Children, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from '@xstyled/styled-components';
import Door from '../door.svg';
import { DoorWrapper } from '../DoorWrapper';
import { Item, ItemDefinition, RenderProps, registry } from '../../../Item';
import { CARD, INFORMATION_DOOR } from '../../../itemIds';
import audioEffectOpenDoor from '~/assets/mp3/door-opening.mp3';
// Removed useAudioEffect to ensure door opening sound always plays for learners regardless of auditory feedback setting
import { t } from 'ttag';
import { useEditorStore } from '~/shell/Store';
import { EditBtn, EditBtnIcon } from '~/editor/buttons/EditBtn';
import DoorEditModal from './DoorEditModal';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { useDoorsStore } from '../../DoorsStore';
const InformationCardWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100% !important;
  min-height: 100% !important;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #000000cc;
  z-index: 99;

  .doorsCard {
    display: block !important;
    position: relative;
    height: 75vh !important;
    max-height: 75vh !important;
    overflow-y: auto !important;

    img {
      max-height: 250px;
      object-fit: cover;
    }
  }

  .cardButton {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 20px;
    margin-bottom: 20px;
  }
`;

const StyledDoor = styled(Door)<{ title?: string }>`
  width: 100%;
  height: 100%;
  -webkit-tap-highlight-color: transparent;
  box-sizing: content-box;
  border-left: 12px solid transparent;
  margin-left: -12px;
`;

interface Props {
  id: string;
  linkedAudioId?: string;
  imageUrl: string;
}

export type InformationDoorDefinition = ItemDefinition<
  typeof INFORMATION_DOOR,
  Props
>;

export class InformationDoor extends Item<typeof INFORMATION_DOOR, Props> {
  protected render({
    children,
    id,
    linkedAudioId,
    imageUrl
  }: RenderProps<Props>) {
    const CARD_ELEMENT = Children.toArray(children)[0];
    const [isDoorOpened, setDoorIsOpened] = useState(false);
    const { editorMode, contentEditable } = useEditorStore();
    const [isCardOpened, setIsCardOpened] = useState(false);
    const doorOpenAudioRef = React.useRef<HTMLAudioElement | null>(null);
    useEffect(() => {
      const audio = new Audio(audioEffectOpenDoor as string);
      audio.preload = 'auto';
      doorOpenAudioRef.current = audio;
    }, []);
    const playDoorOpen = () => {
      const a = doorOpenAudioRef.current;
      if (!a) return;
      try {
        a.currentTime = 0;
        a.play().catch(() => {
          // Intentionaly Empty
        });
      } catch (_) {
        // ignore
      }
    };
    const rootEl = document.getElementById('root');
    const { removeBlocker } = useSequenceStore();
    const { markDoorCompleted, completedDoorIds, doorIds } = useDoorsStore();

    const handleOpenDoor = () => {
      if (!isDoorOpened && !editorMode) {
        playDoorOpen();
        setDoorIsOpened(true);
        setTimeout(() => {
          if (!isDoorOpened) {
            setIsCardOpened(true);
          }
        }, 400);
      } else {
        setIsCardOpened(true);
      }
    };

    useEffect(() => {
      if (rootEl) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        rootEl.inert! = isCardOpened;
      }
    }, [isCardOpened]);

    useEffect(() => {
      const handler = (e: Event) => {
        setIsCardOpened(false);
      };
      window.addEventListener('close-door-card', handler as EventListener);
      return () =>
        window.removeEventListener('close-door-card', handler as EventListener);
    }, [id, markDoorCompleted]);

    useEffect(() => {
      if (doorIds.length && doorIds.every(d => completedDoorIds.includes(d))) {
        doorIds.forEach(d => removeBlocker(d));
      }
    }, [doorIds, completedDoorIds, removeBlocker]);

    // ensure root regains interactivity when component unmounts
    useEffect(() => {
      return () => {
        if (rootEl) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          rootEl.inert! = false;
        }
      };
    }, []);

    return (
      <>
        <DoorWrapper url={imageUrl} onClick={() => handleOpenDoor()}>
          <StyledDoor
            className={isDoorOpened ? 'swing-right-fwd' : ''}
            role="img"
            aria-label={t`Open the door`}
            title={t`Open the door`}
          />
          {editorMode && (
            <EditBtn
              as="div"
              role="button"
              tabIndex={0}
              className="edit-bg-btn"
            >
              <EditBtnIcon />
            </EditBtn>
          )}
        </DoorWrapper>
        {editorMode && (
          <DoorEditModal
            open={isCardOpened && !!editorMode}
            onClose={() => setIsCardOpened(false)}
            cardId={
              React.isValidElement(CARD_ELEMENT)
                ? CARD_ELEMENT.props.id
                : undefined
            }
          />
        )}
        {isCardOpened &&
          !editorMode &&
          ReactDOM.createPortal(
            <InformationCardWrapper>{CARD_ELEMENT}</InformationCardWrapper>,
            document.body
          )}
      </>
    );
  }
}

registry[INFORMATION_DOOR] = InformationDoor;
